"use client";

import React, { useState, useEffect } from 'react';

type SlideData = {
  id: string;
  pre: string;
  title: string;
  sub: React.ReactNode;
  p: React.ReactNode;
  btnText: string;
  btnLink: string;
  bgUrl: string;
  isDark: boolean;
  renderExtra?: (isActive: boolean) => React.ReactNode;
};

type HeroCarouselProps = {
  totalSocios: number;
  ultimosSocios: any[];
  proximoEvento?: any;
  ultimaNoticia?: any;
  ultimoPodcast?: any;
};

export default function HeroCarousel({ totalSocios, ultimosSocios, proximoEvento, ultimaNoticia, ultimoPodcast }: HeroCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Definindo os 6 slides
  const slides: SlideData[] = [
    {
      id: 'guerreiros',
      pre: 'JUNTE-SE AOS',
      title: 'GUERREIROS',
      sub: <>A CASA OFICIAL DO TRICOLOR <br /><span>EM NOVA IGUAÇU</span></>,
      p: <>Mais que uma torcida.<br />Uma família. Uma casa.<br />Guerreiros para sempre!</>,
      btnText: 'VEJA TODOS OS PLANOS',
      btnLink: '#planos',
      bgUrl: 'https://casadoguerreirotricolor.com.br/backhero.webp',
      isDark: false,
      renderExtra: (isActive) => (
        <div className="cgt-proof" style={{ marginTop: '20px', animation: isActive ? 'fadeInUp 0.8s ease 0.4s forwards' : 'none', opacity: 0 }}>
          <div className="cgt-proof-imgs">
            {ultimosSocios.map((socio: any) => (
              socio.foto_url ? (
                <div key={socio.id} className="cgt-proof-img" style={{ backgroundImage: `url(${socio.foto_url})` }}></div>
              ) : (
                <div key={socio.id} className="cgt-proof-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#6b1122', color: '#fff', fontSize: '15px', fontWeight: 'bold', lineHeight: 1, paddingTop: '2px', paddingLeft: '1px', letterSpacing: '0px' }}>
                  {socio.nome ? socio.nome.charAt(0).toUpperCase() : '?'}
                </div>
              )
            ))}
            {ultimosSocios.length < 5 && [...Array(5 - ultimosSocios.length)].map((_, i) => (
              <div key={`fallback-${i}`} className="cgt-proof-img" style={{ backgroundImage: `url(https://i.pravatar.cc/100?img=${i + 10})` }}></div>
            ))}
          </div>
          <div className="cgt-proof-text">
            <span>+{totalSocios.toLocaleString('pt-BR')}</span> GUERREIROS JÁ FAZEM PARTE
          </div>
        </div>
      )
    },
    {
      id: 'eventos',
      pre: 'VENHA PARA OS',
      title: 'PRÓXIMOS EVENTOS',
      sub: proximoEvento ? <>NÃO FIQUE DE FORA DO <br /><span>{proximoEvento.titulo}</span></> : <>ACOMPANHE TODOS OS JOGOS <br /><span>COM A NOSSA TORCIDA</span></>,
      p: proximoEvento ? <>Data: {new Date(proximoEvento.data_evento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} <br />Local: Casa do Guerreiro Tricolor<br />Garanta já o seu lugar!</> : <>As melhores festas e transmissões<br />Você encontra aqui na Casa.<br />Traga sua família!</>,
      btnText: 'VER AGENDA COMPLETA',
      btnLink: '/eventos',
      bgUrl: proximoEvento?.imagem_url ? `https://api.arfainfo.com.br${proximoEvento.imagem_url}` : 'https://images.unsplash.com/photo-1522778119026-d647f0596c20?auto=format&fit=crop&w=1920&q=80',
      isDark: true,
    },
    {
      id: 'podcasts',
      pre: 'ASSISTA AOS NOSSOS',
      title: 'PODCASTS',
      sub: ultimoPodcast ? <>ÚLTIMO EPISÓDIO: <br /><span>{ultimoPodcast.snippet.title.replace(/&quot;/g, '"')}</span></> : <>PAPOS DE FLU E <br /><span>RESENHAS EXCLUSIVAS</span></>,
      p: <>Entrevistas, debates e muita resenha.<br />Inscreva-se no nosso canal do YouTube<br />e não perca nada!</>,
      btnText: 'ASSISTIR AGORA',
      btnLink: '/podcasts',
      bgUrl: ultimoPodcast?.snippet?.thumbnails?.high?.url || 'https://images.unsplash.com/photo-1589903308904-1010c2294adc?auto=format&fit=crop&w=1920&q=80',
      isDark: true,
    },
    {
      id: 'flu',
      pre: 'SITE OFICIAL DO',
      title: 'FLUMINENSE',
      sub: <>ACOMPANHE O DIA A DIA <br /><span>DO NOSSO TIME DO CORAÇÃO</span></>,
      p: <>Acesse a plataforma oficial do Fluminense.<br />Ingressos, notícias do elenco, ST<br />e muito mais.</>,
      btnText: 'ACESSAR FLUMINENSE.COM.BR',
      btnLink: 'https://www.fluminense.com.br/',
      bgUrl: 'https://images.unsplash.com/photo-1518091043644-c1d4457512c6?auto=format&fit=crop&w=1920&q=80',
      isDark: true,
    },
    {
      id: 'noticias',
      pre: 'FIQUE POR DENTRO DAS',
      title: 'NOTÍCIAS',
      sub: ultimaNoticia ? <>{ultimaNoticia.titulo.toUpperCase()}</> : <>SAIBA TUDO QUE ACONTECE <br /><span>NA NOSSA CASA</span></>,
      p: ultimaNoticia ? <>{ultimaNoticia.resumo || 'Clique para ler mais sobre essa novidade.'}</> : <>Mantenha-se informado sobre os eventos,<br />comunicados e avisos importantes.<br />A informação oficial passa por aqui!</>,
      btnText: 'LER TODAS AS NOTÍCIAS',
      btnLink: '/noticias',
      bgUrl: ultimaNoticia?.imagem_url ? `https://api.arfainfo.com.br${ultimaNoticia.imagem_url}` : 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?auto=format&fit=crop&w=1920&q=80',
      isDark: true,
    },
    {
      id: 'loja',
      pre: 'VISTA A ARMADURA NA',
      title: 'NOSSA LOJA',
      sub: <>PRODUTOS OFICIAIS <br /><span>E EXCLUSIVOS DA CASA</span></>,
      p: <>Camisas, bonés, copos e muito mais.<br />Leve a paixão tricolor no peito<br />e ajude a fortalecer a torcida.</>,
      btnText: 'VISITAR A LOJA OFICIAL',
      btnLink: '/loja',
      bgUrl: 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=1920&q=80',
      isDark: true,
    }
  ];

  useEffect(() => {
    // No celular, ignoramos o hover para não pausar o carrossel permanentemente após um toque
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    if (isHovered && !isMobile) return;

    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 10000); // 10 segundos
    
    return () => clearTimeout(timer);
  }, [slides.length, currentSlide, isHovered]);

  return (
    <section 
      className="cgt-hero-wrapper" 
      style={{ position: 'relative', overflow: 'hidden', display: 'grid' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <style>{`
        @keyframes fadeInLeft {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {slides.map((slide, index) => {
        const isActive = index === currentSlide;
        return (
          <div
            key={slide.id}
            className="cgt-hero-slide"
            style={{
              gridArea: '1 / 1',
              opacity: isActive ? 1 : 0,
              visibility: isActive ? 'visible' : 'hidden',
              transition: 'opacity 1s ease-in-out, visibility 1s ease-in-out',
              display: 'flex',
              alignItems: 'center',
              padding: '60px 0 140px 0',
              zIndex: isActive ? 10 : 1,
              position: 'relative'
            }}
          >
            {/* Background Overlay */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: slide.isDark 
                ? `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.85)), url('${slide.bgUrl}') center/cover no-repeat`
                : `url('${slide.bgUrl}') center/cover no-repeat`,
              opacity: slide.isDark ? 1 : 0.35,
              mixBlendMode: slide.isDark ? 'normal' : 'multiply',
              WebkitMaskImage: 'linear-gradient(to top, transparent, black 250px)',
              maskImage: 'linear-gradient(to top, transparent, black 250px)',
              zIndex: 0
            }}></div>


            <div className="cgt-container cgt-hero-content" style={{ width: '100%', zIndex: 10 }}>
              <div className="cgt-hero-left" style={{ animation: isActive ? 'fadeInLeft 0.8s ease forwards' : 'none', opacity: 0 }}>
                <div className="cgt-hero-pre cgt-title-font" style={{ color: slide.isDark ? '#00c96e' : '#6b1122', marginBottom: '-5px' }}>
                  {slide.pre}
                </div>
                <h1 className="cgt-hero-title cgt-title-font" style={{ color: slide.isDark ? '#fff' : '#6b1122', textShadow: slide.isDark ? '4px 4px 0px #6b1122' : '2px 2px 0px rgba(255,255,255,0.3)', paddingTop: '10px' }}>
                  {slide.title}
                </h1>
                <div className="cgt-hero-sub" style={{ color: slide.isDark ? '#fff' : '#6b1122' }}>
                  {slide.sub}
                </div>
                <p className="cgt-hero-p" style={{ color: slide.isDark ? '#eee' : '#444', textShadow: slide.isDark ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none' }}>
                  {slide.p}
                </p>

                <a href={slide.btnLink} className="cgt-btn-red" style={{ animation: isActive ? 'fadeInUp 0.8s ease 0.2s forwards' : 'none', opacity: 0 }}>
                  {slide.btnText} <span>›</span>
                </a>

                {slide.renderExtra && slide.renderExtra(isActive)}
              </div>
            </div>
          </div>
        );
      })}

      {/* Navegação Manual (Dots) */}
      <div style={{ position: 'absolute', bottom: '30px', left: 0, width: '100%', display: 'flex', justifyContent: 'center', gap: '10px', zIndex: 20 }}>
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: index === currentSlide ? '#00c96e' : 'rgba(255, 255, 255, 0.4)',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease',
              padding: 0
            }}
            aria-label={`Ir para o slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Setas Laterais */}
      <button 
        onClick={() => setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1))}
        style={{
          position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)',
          background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.2)', color: 'white',
          width: '44px', height: '44px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', zIndex: 20, transition: 'background 0.3s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = '#6b1122'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
      >
        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
      </button>
      <button 
        onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
        style={{
          position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)',
          background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.2)', color: 'white',
          width: '44px', height: '44px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', zIndex: 20, transition: 'background 0.3s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = '#6b1122'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
      >
        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
      </button>
    </section>
  );
}
